import {
  useMemo,
  useRef,
  CSSProperties,
  ImgHTMLAttributes,
  forwardRef,
  ForwardedRef,
} from 'react';
import styles from './PageImage.module.scss';
import {DEFAULT_THUMBNAIL} from '../../commonSub/constant';
import useLazyLoad from '../hook/useLazyLoad';

interface IProps
  extends Omit<
    ImgHTMLAttributes<HTMLImageElement>,
    'src' | 'alt' | 'onError' | 'onLoad'
  > {
  src: string;
  isLazy?: boolean;
  alt: string;
  pictureStyle?: CSSProperties;
  pictureClassName?: string;
  // TODO: dataset 타입 정의가 힘들다. 나중에 수정하자.
  pictureProps?: object;
  onLoad?: (isLoad: boolean) => void;
}

// TODO: swiper에서 loop를 사용하면 이미지 loop될 때 로딩이 안된다.
function PageImage(
  {
    src,
    isLazy = true,
    alt,
    pictureStyle,
    pictureClassName,
    pictureProps,
    onLoad,
    ...imageProps
  }: IProps,
  ref: ForwardedRef<HTMLPictureElement>
) {
  const imgRef = useRef<HTMLImageElement>(null);
  const {canLoad, setIsError} = useLazyLoad<HTMLImageElement>(isLazy, imgRef);

  const sources = useMemo(() => parseSrc(src), [src]);
  const defaultSrc =
    sources.length > 0 ? sources[sources.length - 1].path : src;

  return (
    <picture
      className={cn(pictureClassName, !canLoad && DEFAULT_IMAGE_CN)}
      style={pictureStyle}
      ref={ref}
      {...pictureProps}
    >
      {canLoad &&
        sources.map(source => (
          <source key={source.type} type={source.type} srcSet={source.path} />
        ))}
      <img
        ref={imgRef}
        src={canLoad ? defaultSrc : DEFAULT_THUMBNAIL}
        alt={alt}
        onLoad={canLoad && onLoad ? () => onLoad(true) : undefined}
        onError={() => setIsError(true)}
        {...imageProps}
      />
    </picture>
  );
}

export default forwardRef(PageImage);

export function parseSrc(src: string) {
  if (!src) {
    return [];
  }
  const lastIndex = src.lastIndexOf('/');
  const types = src.substring(lastIndex + 1).split('.');
  const filename = types.shift();
  if (types.length > 0) {
    const path = src.substring(0, lastIndex + 1);
    return types.map(type => ({
      type: getImageMimeType(type),
      path: `${path}${filename}.${type}`,
    }));
  } else {
    return [];
  }
}

function getImageMimeType(ext: string) {
  const type = IMAGE_MIME_TYPE_MAP[ext];
  if (type) {
    return `image/${type}`;
  } else {
    return `image/${ext}`;
  }
}
// 확장자명과 mime-type 이름이 다른 경우에는 아래 map에 기록한다
// 참고 링크: https://developer.mozilla.org/ko/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Complete_list_of_MIME_types
const IMAGE_MIME_TYPE_MAP: {[key: string]: string} = {
  jpg: 'jpeg',
  ico: 'x-icon',
  svg: 'svg+xml',
  tif: 'tiff',
};

const DEFAULT_IMAGE_CN = cn(
  styles.showDefault,
  'block h-full opacity-0 relative w-full',
  'after:h-44 after:left-1/2',
  'after:-mx-22 after:pointer-events-none after:absolute after:top-1/2 after:w-44'
);
